import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { subCategoryAPI, budgetPlanAPI, vendorMatchAPI } from '../lib/api';
import { 
  Calculator, 
  Save, 
  Download, 
  Mail, 
  Loader2, 
  Plus,
  ChevronDown,
  ChevronUp,
  Store,
  ArrowLeft,
  Pencil,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';

// PAX options
const paxOptions = [
  { value: 'A', label: 'Pax A (100-200 people)' },
  { value: 'B', label: 'Pax B (200-400 people)' },
  { value: 'C', label: 'Pax C (400-500 people)' },
  { value: 'D', label: 'Pax D (500-1000 people)' },
  { value: 'E', label: 'Pax E (more than 1000 people)' },
];

const getPaxLabel = (pax) => {
  const option = paxOptions.find(p => p.value === pax);
  return option ? option.label : `Pax ${pax}`;
};

// Format currency to Rupiah
const formatRupiah = (amount) => {
  return `Rp ${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const BudgetPlanner = () => {
  const [subCategories, setSubCategories] = useState([]);
  const [budgetPlans, setBudgetPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'edit'
  const [planToDelete, setPlanToDelete] = useState(null);
  
  // Budget Plan State
  const [planData, setPlanData] = useState({
    id: null,
    name: '',
    client_name: '',
    client_email: '',
    total_budget: 50000000,
    pax: 'A',
    notes: '',
  });
  const [allocations, setAllocations] = useState([]);
  const [vendorMatches, setVendorMatches] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subCategoriesRes, budgetPlansRes] = await Promise.all([
        subCategoryAPI.getAll(),
        budgetPlanAPI.getAll(),
      ]);
      setSubCategories(subCategoriesRes.data);
      setBudgetPlans(budgetPlansRes.data);

      // Group sub-categories by category
      const categoryGroups = {};
      subCategoriesRes.data.forEach(sc => {
        if (!categoryGroups[sc.category_name]) {
          categoryGroups[sc.category_name] = true;
        }
      });
      setExpandedCategories(categoryGroups);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const initializeAllocations = (subCats, totalBudget) => {
    const totalDefaultPercentage = subCats.reduce((sum, sc) => sum + sc.default_percentage, 0);
    const allocs = subCats.map(sc => {
      const percentage = totalDefaultPercentage > 0 ? (sc.default_percentage / totalDefaultPercentage) * 100 : (100 / subCats.length);
      return {
        sub_category_id: sc.id,
        sub_category_name: sc.name,
        category_name: sc.category_name,
        percentage: percentage,
        amount: totalBudget * (percentage / 100),
      };
    });
    setAllocations(allocs);
  };

  const handleCreateNew = () => {
    setPlanData({
      id: null,
      name: '',
      client_name: '',
      client_email: '',
      total_budget: 50000000,
      pax: 'A',
      notes: '',
    });
    initializeAllocations(subCategories, 50000000);
    setVendorMatches([]);
    setViewMode('edit');
  };

  const handleEditPlan = async (plan) => {
    setPlanData({
      id: plan.id,
      name: plan.name,
      client_name: plan.client_name,
      client_email: plan.client_email,
      total_budget: plan.total_budget,
      pax: plan.pax || 'A',
      notes: plan.notes,
    });
    
    // Map allocations
    const allocs = plan.allocations.map(alloc => {
      const sc = subCategories.find(s => s.id === alloc.sub_category_id);
      return {
        sub_category_id: alloc.sub_category_id,
        sub_category_name: sc?.name || '',
        category_name: sc?.category_name || '',
        percentage: alloc.percentage,
        amount: alloc.amount,
      };
    });
    setAllocations(allocs);
    setVendorMatches(plan.vendor_matches || []);
    setViewMode('edit');
  };

  const handleBackToList = () => {
    setViewMode('list');
    fetchData();
  };

  const handleBudgetChange = (value) => {
    const budget = parseFloat(value) || 0;
    setPlanData(prev => ({ ...prev, total_budget: budget }));
    
    // Recalculate amounts based on percentages
    setAllocations(prev => prev.map(alloc => ({
      ...alloc,
      amount: (alloc.percentage / 100) * budget,
    })));
  };

  // Handle amount change - percentage is calculated automatically
  const handleAmountChange = (subCategoryId, newAmount) => {
    const amount = parseFloat(newAmount) || 0;
    setAllocations(prev => {
      const updated = prev.map(alloc => {
        if (alloc.sub_category_id === subCategoryId) {
          const percentage = planData.total_budget > 0 ? (amount / planData.total_budget) * 100 : 0;
          return {
            ...alloc,
            amount: amount,
            percentage: percentage,
          };
        }
        return alloc;
      });
      return updated;
    });
  };

  const handleMatchVendors = async () => {
    try {
      const allocData = allocations.map(a => ({
        sub_category_id: a.sub_category_id,
        percentage: a.percentage,
        amount: a.amount,
      }));
      
      const response = await vendorMatchAPI.match({ allocations: allocData, pax: planData.pax });
      setVendorMatches(response.data);
      toast.success('Vendors matched successfully');
    } catch (error) {
      toast.error('Failed to match vendors');
    }
  };

  const handleSave = async () => {
    if (!planData.name.trim()) {
      toast.error('Please enter a plan name');
      return;
    }

    if (!planData.client_name.trim()) {
      toast.error('Please enter client name');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: planData.name,
        client_name: planData.client_name,
        client_email: planData.client_email,
        total_budget: planData.total_budget,
        pax: planData.pax,
        notes: planData.notes,
        allocations: allocations.map(a => ({
          sub_category_id: a.sub_category_id,
          percentage: a.percentage,
          amount: a.amount,
        })),
      };

      let response;
      if (planData.id) {
        response = await budgetPlanAPI.update(planData.id, data);
        toast.success('Budget plan updated');
      } else {
        response = await budgetPlanAPI.create(data);
        toast.success('Budget plan created');
        setPlanData(prev => ({ ...prev, id: response.data.id }));
      }
      
      setVendorMatches(response.data.vendor_matches || []);
      
      // Refresh plans list
      const plansRes = await budgetPlanAPI.getAll();
      setBudgetPlans(plansRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save budget plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!planData.id) {
      toast.error('Please save the plan first');
      return;
    }

    try {
      const response = await budgetPlanAPI.downloadPdf(planData.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Format: planname_yyyymmdd
      const today = new Date();
      const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '');
      const safePlanName = planData.name.replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `${safePlanName}_${dateStr}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendEmail = async () => {
    if (!emailAddress.trim()) {
      toast.error('Please enter email address');
      return;
    }

    setSendingEmail(true);
    try {
      await budgetPlanAPI.sendEmail(planData.id, emailAddress);
      toast.success(`Email sent to ${emailAddress}`);
      setEmailDialogOpen(false);
      setEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    
    try {
      await budgetPlanAPI.delete(planToDelete.id);
      toast.success('Budget plan deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete budget plan');
    } finally {
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    }
  };

  const openDeleteDialog = (plan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const totalAllocated = allocations.reduce((sum, a) => sum + a.amount, 0);
  const totalPercentage = planData.total_budget > 0 ? (totalAllocated / planData.total_budget) * 100 : 0;
  const remainingBudget = planData.total_budget - totalAllocated;

  // Group allocations by category
  const allocationsByCategory = allocations.reduce((acc, alloc) => {
    if (!acc[alloc.category_name]) {
      acc[alloc.category_name] = [];
    }
    acc[alloc.category_name].push(alloc);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // LIST VIEW - Show table of plans
  if (viewMode === 'list') {
    return (
      <AdminLayout>
        <div className="animate-fade-in" data-testid="budget-planner-page">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-serif text-3xl font-medium text-[#1A1A1A] tracking-tight">
                Budget Planner
              </h1>
              <p className="text-[#585858] mt-2">
                Manage wedding budget plans
              </p>
            </div>
            <Button
              onClick={handleCreateNew}
              disabled={subCategories.length === 0}
              className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm"
              data-testid="create-new-plan-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New Plan
            </Button>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#69111F]" />
            </div>
          ) : subCategories.length === 0 ? (
            <Card className="bg-white border-[#E6DCD3]">
              <CardContent className="p-12 text-center">
                <p className="text-[#585858]">
                  Please create categories and sub-categories first to start budget planning.
                </p>
              </CardContent>
            </Card>
          ) : budgetPlans.length === 0 ? (
            <Card className="bg-white border-[#E6DCD3]">
              <CardContent className="p-12 text-center">
                <Calculator className="w-12 h-12 mx-auto text-[#69111F] mb-4" />
                <p className="text-[#585858] mb-4">
                  No budget plans yet. Create your first plan to get started.
                </p>
                <Button
                  onClick={handleCreateNew}
                  className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8]"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create First Plan
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="bg-white border border-[#E6DCD3] rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F2EBE3] hover:bg-[#F2EBE3]">
                    <TableHead className="font-serif text-[#69111F] font-medium">Plan Name</TableHead>
                    <TableHead className="font-serif text-[#69111F] font-medium">Couple Name</TableHead>
                    <TableHead className="font-serif text-[#69111F] font-medium">Pax</TableHead>
                    <TableHead className="font-serif text-[#69111F] font-medium">Total Budget</TableHead>
                    <TableHead className="font-serif text-[#69111F] font-medium">Created Date</TableHead>
                    <TableHead className="font-serif text-[#69111F] font-medium text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgetPlans.map((plan) => (
                    <TableRow key={plan.id} className="hover:bg-[#FDFBF8]">
                      <TableCell className="font-medium text-[#1A1A1A]">{plan.name}</TableCell>
                      <TableCell className="text-[#585858]">{plan.client_name}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 text-xs font-medium bg-[#F2EBE3] text-[#69111F] rounded-sm">
                          Pax {plan.pax || 'A'}
                        </span>
                      </TableCell>
                      <TableCell className="font-serif text-[#69111F]">
                        {formatRupiah(plan.total_budget)}
                      </TableCell>
                      <TableCell className="text-[#585858]">
                        {new Date(plan.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditPlan(plan)}
                            className="text-[#69111F] hover:bg-[#69111F]/10"
                            data-testid={`edit-plan-${plan.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(plan)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-plan-${plan.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-[#1A1A1A]">
                  Delete Budget Plan
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[#585858]">
                  Are you sure you want to delete "{planToDelete?.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-[#E6DCD3]">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeletePlan}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-plan-btn"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </AdminLayout>
    );
  }

  // EDIT VIEW - Edit/Create plan
  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="budget-planner-edit-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleBackToList}
              className="text-[#69111F] hover:bg-[#69111F]/10"
              data-testid="back-to-list-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-serif text-3xl font-medium text-[#1A1A1A] tracking-tight">
                {planData.id ? 'Edit Budget Plan' : 'Create Budget Plan'}
              </h1>
              <p className="text-[#585858] mt-1">
                {planData.id ? `Editing: ${planData.name}` : 'Create a new wedding budget plan'}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Plan Details & Allocations */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plan Details */}
            <Card className="bg-white border-[#E6DCD3]">
              <CardHeader className="border-b border-[#E6DCD3]">
                <CardTitle className="font-serif text-xl text-[#1A1A1A]">
                  Plan Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#1A1A1A]">Plan Name *</Label>
                    <Input
                      value={planData.name}
                      onChange={(e) => setPlanData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Smith Wedding 2024"
                      className="border-[#E6DCD3] focus:border-[#69111F]"
                      data-testid="plan-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1A1A1A]">Couple Name *</Label>
                    <Input
                      value={planData.client_name}
                      onChange={(e) => setPlanData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="John & Jane Smith"
                      className="border-[#E6DCD3] focus:border-[#69111F]"
                      data-testid="client-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#1A1A1A]">Client Email</Label>
                    <Input
                      type="email"
                      value={planData.client_email}
                      onChange={(e) => setPlanData(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="client@example.com"
                      className="border-[#E6DCD3] focus:border-[#69111F]"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1A1A1A]">Pax (Number of Guests) *</Label>
                    <Select 
                      value={planData.pax} 
                      onValueChange={(value) => setPlanData(prev => ({ ...prev, pax: value }))}
                    >
                      <SelectTrigger className="border-[#E6DCD3]" data-testid="plan-pax-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paxOptions.map((pax) => (
                          <SelectItem key={pax.value} value={pax.value}>{pax.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A1A1A] text-lg">Total Wedding Budget</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#69111F] font-serif text-xl">Rp</span>
                    <Input
                      type="number"
                      value={planData.total_budget}
                      onChange={(e) => handleBudgetChange(e.target.value)}
                      className="pl-12 h-14 text-2xl font-serif text-[#69111F] border-[#E6DCD3] focus:border-[#69111F]"
                      data-testid="total-budget-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A1A1A]">Notes</Label>
                  <Textarea
                    value={planData.notes}
                    onChange={(e) => setPlanData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes for this plan..."
                    className="border-[#E6DCD3] focus:border-[#69111F] min-h-[80px]"
                    data-testid="plan-notes-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Budget Allocations */}
            <Card className="bg-white border-[#E6DCD3]">
              <CardHeader className="border-b border-[#E6DCD3]">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-xl text-[#1A1A1A]">
                    Budget Allocations
                  </CardTitle>
                  <div className="flex items-center gap-4">
                    <span className={`text-sm ${remainingBudget < 0 ? 'text-red-600' : 'text-[#585858]'}`}>
                      Total: {totalPercentage.toFixed(1)}% 
                      {remainingBudget !== 0 && ` (${formatRupiah(Math.abs(remainingBudget))} ${remainingBudget > 0 ? 'remaining' : 'over'})`}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {Object.entries(allocationsByCategory).map(([category, categoryAllocations]) => (
                  <Collapsible 
                    key={category} 
                    open={expandedCategories[category]} 
                    onOpenChange={() => toggleCategory(category)}
                  >
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between p-4 bg-[#F2EBE3] cursor-pointer hover:bg-[#EBE4DC] transition-colors border-b border-[#E6DCD3]">
                        <div className="flex items-center gap-2">
                          {expandedCategories[category] ? (
                            <ChevronUp className="w-5 h-5 text-[#69111F]" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#69111F]" />
                          )}
                          <span className="font-serif font-medium text-[#69111F]">{category}</span>
                        </div>
                        <span className="font-serif text-[#69111F]">
                          {formatRupiah(categoryAllocations.reduce((sum, a) => sum + a.amount, 0))}
                        </span>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {categoryAllocations.map((alloc) => {
                        const vendorMatch = vendorMatches.find(v => v.sub_category_id === alloc.sub_category_id);
                        return (
                          <div key={alloc.sub_category_id} className="p-4 border-b border-[#E6DCD3] hover:bg-[#FDFBF8]">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-[#1A1A1A]">{alloc.sub_category_name}</span>
                              <div className="flex items-center gap-3">
                                {/* Percentage display (disabled) */}
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="text"
                                    value={alloc.percentage.toFixed(1)}
                                    disabled
                                    className="w-16 h-8 text-center border-[#E6DCD3] bg-gray-100 text-[#8A8A8A]"
                                  />
                                  <span className="text-[#8A8A8A]">%</span>
                                </div>
                                {/* Amount input (editable) */}
                                <div className="flex items-center gap-1">
                                  <span className="text-[#69111F] font-serif">Rp</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={alloc.amount}
                                    onChange={(e) => handleAmountChange(alloc.sub_category_id, e.target.value)}
                                    className="w-32 h-8 text-right border-[#E6DCD3] font-serif text-[#69111F]"
                                    data-testid={`allocation-amount-${alloc.sub_category_id}`}
                                  />
                                </div>
                              </div>
                            </div>
                            {/* Progress bar showing percentage of total */}
                            <div className="h-2 bg-[#E6DCD3] rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#69111F] rounded-full transition-all duration-300"
                                style={{ width: `${Math.min(alloc.percentage * 2, 100)}%` }}
                              />
                            </div>
                            {vendorMatch && vendorMatch.vendor_name && (
                              <div className="mt-3 p-3 bg-[#FDFBF8] border border-[#E6DCD3] rounded-sm">
                                <div className="flex items-center gap-2 text-sm">
                                  <Store className="w-4 h-4 text-[#69111F]" />
                                  <span className="font-medium text-[#69111F]">{vendorMatch.vendor_name}</span>
                                  <span className="text-[#585858]">|</span>
                                  <span className="text-[#585858]">{vendorMatch.vendor_price_tier}</span>
                                  {vendorMatch.vendor_pax && (
                                    <>
                                      <span className="text-[#585858]">|</span>
                                      <span className="text-[#585858]">Pax {vendorMatch.vendor_pax}</span>
                                    </>
                                  )}
                                  <span className="text-[#585858]">|</span>
                                  <span className="text-[#585858]">{vendorMatch.vendor_price_range}</span>
                                </div>
                                {vendorMatch.vendor_contact && (
                                  <p className="text-xs text-[#8A8A8A] mt-1">Contact: {vendorMatch.vendor_contact}</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Actions & Summary */}
          <div className="space-y-6">
            {/* Actions */}
            <Card className="bg-white border-[#E6DCD3]">
              <CardHeader className="border-b border-[#E6DCD3]">
                <CardTitle className="font-serif text-xl text-[#1A1A1A]">
                  Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full h-12 bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm"
                  data-testid="save-plan-btn"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
                  Save Plan
                </Button>
                <Button
                  onClick={handleMatchVendors}
                  variant="outline"
                  className="w-full h-12 border-[#69111F] text-[#69111F] hover:bg-[#69111F] hover:text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm"
                  data-testid="match-vendors-btn"
                >
                  <Store className="w-5 h-5 mr-2" />
                  Match Vendors
                </Button>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleDownloadPdf}
                    disabled={!planData.id}
                    variant="outline"
                    className="h-12 border-[#E6DCD3] text-[#585858] hover:border-[#69111F] hover:text-[#69111F] rounded-sm"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                  <Button
                    onClick={() => {
                      if (!planData.id) {
                        toast.error('Please save the plan first');
                        return;
                      }
                      setEmailAddress(planData.client_email || '');
                      setEmailDialogOpen(true);
                    }}
                    disabled={!planData.id}
                    variant="outline"
                    className="h-12 border-[#E6DCD3] text-[#585858] hover:border-[#69111F] hover:text-[#69111F] rounded-sm"
                    data-testid="send-email-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Budget Summary */}
            <Card className="bg-white border-[#E6DCD3]">
              <CardHeader className="border-b border-[#E6DCD3]">
                <CardTitle className="font-serif text-xl text-[#1A1A1A]">
                  Budget Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-[#585858] uppercase tracking-widest mb-2">Total Budget</p>
                  <p className="font-serif text-3xl text-[#69111F]">
                    {formatRupiah(planData.total_budget)}
                  </p>
                  <p className="text-sm text-[#585858] mt-2">{getPaxLabel(planData.pax)}</p>
                </div>
                <div className="space-y-3">
                  {Object.entries(allocationsByCategory).map(([category, categoryAllocations]) => {
                    const categoryTotal = categoryAllocations.reduce((sum, a) => sum + a.amount, 0);
                    const categoryPercentage = planData.total_budget > 0 ? (categoryTotal / planData.total_budget) * 100 : 0;
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-[#585858]">{category}</span>
                          <span className="font-medium text-[#1A1A1A]">
                            {formatRupiah(categoryTotal)}
                          </span>
                        </div>
                        <div className="h-2 bg-[#E6DCD3] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#69111F] rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(categoryPercentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 pt-6 border-t border-[#E6DCD3]">
                  <div className="flex items-center justify-between">
                    <span className="text-[#585858]">Allocated</span>
                    <span className="font-serif text-lg text-[#69111F]">
                      {formatRupiah(totalAllocated)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[#585858]">Remaining</span>
                    <span className={`font-serif text-lg ${remainingBudget < 0 ? 'text-red-600' : 'text-[#3D5A46]'}`}>
                      {formatRupiah(remainingBudget)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vendor Matches Summary */}
            {vendorMatches.length > 0 && (
              <Card className="bg-white border-[#E6DCD3]">
                <CardHeader className="border-b border-[#E6DCD3]">
                  <CardTitle className="font-serif text-xl text-[#1A1A1A]">
                    Vendor Matches ({vendorMatches.filter(v => v.vendor_name).length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-80 overflow-y-auto">
                  {vendorMatches.filter(v => v.vendor_name).map((match) => (
                    <div key={match.sub_category_id} className="p-4 border-b border-[#E6DCD3] last:border-b-0">
                      <p className="text-sm text-[#585858]">{match.sub_category_name}</p>
                      <p className="font-medium text-[#69111F]">{match.vendor_name}</p>
                      <p className="text-xs text-[#8A8A8A]">{match.vendor_price_range}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="sm:max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-[#1A1A1A]">
                Send Budget Plan via Email
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label className="text-[#1A1A1A]">Recipient Email *</Label>
              <Input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="client@example.com"
                className="mt-2 border-[#E6DCD3] focus:border-[#69111F]"
                data-testid="email-recipient-input"
              />
              <p className="text-sm text-[#8A8A8A] mt-2">
                The budget plan PDF will be attached to the email.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEmailDialogOpen(false)}
                className="border-[#E6DCD3]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8]"
                data-testid="confirm-send-email-btn"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default BudgetPlanner;
